import { storage } from '@/lib/storage';
import { z } from 'zod';
import { createLogger } from '@/lib/logger';

const log = createLogger('auth/token');

/**
 * Claims signed by the backend (`TokenPayloadData`). `role` is the global
 * platform role (ADMIN = super admin, CLIENT = regular user) — not the
 * per-organization ADMIN/EDITOR/VIEWER role. `.passthrough()` keeps the
 * standard JWT claims (exp, iat) accessible.
 */
const TokenPayloadSchema = z
  .object({
    sub: z.string().min(1),
    email: z.string(),
    tenantSlug: z.string().min(1),
    role: z.enum(['ADMIN', 'CLIENT']),
  })
  .passthrough();

export type TokenPayload = z.infer<typeof TokenPayloadSchema>;
export type GlobalRole = TokenPayload['role'];

function base64UrlDecode(segment: string): string {
  return atob(segment.replace(/-/g, '+').replace(/_/g, '/'));
}

export function decodeAccessToken(token: string): TokenPayload | null {
  const segments = token.split('.');
  if (segments.length !== 3) {
    log.error('Malformed JWT: expected 3 segments');
    return null;
  }

  let raw: unknown;
  try {
    raw = JSON.parse(base64UrlDecode(segments[1]));
  } catch (error) {
    log.error('Error parsing token payload', error);
    return null;
  }

  const result = TokenPayloadSchema.safeParse(raw);
  if (!result.success) {
    log.error('Unexpected token payload shape', result.error);
    return null;
  }
  return result.data;
}

/**
 * Plain localStorage read — NOT a hook: it does not re-render when the
 * token changes (a full auth state refactor is tracked in TECH_DEBT.md).
 */
export function getUserRole(): GlobalRole | null {
  const token = storage.get('accessToken');
  if (!token) return null;
  return decodeAccessToken(token)?.role ?? null;
}
